import React, { useEffect, useMemo, useState } from 'react';
import { Project, ProjectsFilter } from '../../data/models/project';
import dayjs from 'dayjs';
import { GridColDef } from '@mui/x-data-grid';
import Table from '../common/Table';
import { GridToolbarContainer } from '@mui/x-data-grid';
import Link from '../common/Link';
import ProjectStatusDisplay from '../projects/ProjectStatusDisplay';
import { fetchProjects } from '../../services/project-service';
import Paper, { PaperHeader } from '../common/layout/Paper';
import { useNavigate } from 'react-router-dom';
import ProjectTypeDisplay from '../projects/ProjectTypeDisplay';
import ToggleButton from '../common/buttons/ToggleButton';
import { Box, styled, useTheme, useMediaQuery } from '@mui/material';

export function RecentProjects() {
	const navigate = useNavigate();
	const [filterType, setFilterType] = useState<string>('ACTIVE');
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

	const [projects, setProjects] = useState<{
		list: Project[];
		count: number;
		initialized: boolean;
	}>({ list: [], count: 0, initialized: false });

	useEffect(() => {
		const initializeProjects = async () => {
			const projectsFilter: ProjectsFilter = {
				searchText: '',
				page: 0,
				take: 5,
				order: 'created',
				direction: 'desc',
				where: {
					status: filterType === 'ACTIVE' ? ['ACTIVE'] : ['ARCHIVED'],
				},
			};
			const response = await fetchProjects(projectsFilter);
			setProjects({ ...response, initialized: true });
		};
		initializeProjects();
	}, [fetchProjects, filterType]);

	const columns: GridColDef[] = useMemo(() => {
		return [
			{
				field: 'namespace',
				headerName: 'Namespace',
				minWidth: 325,
				flex: 1,
				disableColumnMenu: true,
				sortable: false,
				renderCell: (params: any) => {
					return (
						<Link
							onClick={() => navigate(`/projects/${params.id}`)}
						>
							{params.value}
						</Link>
					);
				},
			},
			{
				field: 'client',
				headerName: 'Client',
				minWidth: 200,
				flex: 1,
				disableColumnMenu: true,
				renderCell: (params: any) => {
					return (
						<Link
							onClick={() =>
								navigate(`/clients/${params.value.id}`)
							}
						>
							{params.value?.name}
						</Link>
					);
				},
				hide: isMobile,
			},
			{
				field: 'name',
				headerName: 'Name',
				minWidth: 200,
				flex: 1,
				disableColumnMenu: true,
				hide: isMobile,
			},
			{
				field: 'number',
				headerName: '#',
				minWidth: 50,
				disableColumnMenu: true,
				hide: isMobile,
			},
			{
				field: 'type',
				headerName: 'Type',
				minWidth: 150,
				flex: 0.7,
				disableColumnMenu: true,
				renderCell: (params: any) => (
					<ProjectTypeDisplay type={params.value} />
				),
				hide: isTablet,
			},
			{
				field: 'status',
				headerName: 'Status',
				minWidth: 140,
				flex: 0.5,
				disableColumnMenu: true,
				sortable: false,
				renderCell: (params: any) => (
					<ProjectStatusDisplay
						status={params.value}
						subStatus={params.row.subStatus}
					/>
				),
			},
			{
				field: 'createdBy',
				headerName: 'Created By',
				minWidth: 200,
				flex: 0.8,
				disableColumnMenu: true,
				sortable: false,
				renderCell: (params: any) => <div>{params.value?.name}</div>,
				hide: isTablet,
			},
			{
				field: 'created',
				headerName: 'Created On',
				minWidth: 140,
				flex: 0.7,
				disableColumnMenu: true,
				sortable: false,
				renderCell: (params: any) =>
					dayjs(params.value).format('MMM D, h:mm A'),
				hide: isMobile,
			},
		];
	}, [isMobile, isTablet, navigate]);

	const rows: any[] = useMemo(() => {
		return projects.list.map((project) => ({
			id: project.id,
			namespace: project.namespace,
			client: project.client,
			name: project.name,
			number: project.number,
			type: project.type,
			status: project.status,
			subStatus: project.subStatus,
			created: project.created,
			createdBy: project.createdBy,
		}));
	}, [projects.list]);

	return (
		<Paper gradientBorder>
			<Table
				columns={columns}
				rows={rows}
				page={0}
				pageSize={10}
				rowCount={projects.count}
				sortModel={[{ field: 'created', sort: 'desc' }]}
				onSortModelChange={() => {}}
				onPaginationModelChange={() => {}}
				hideFooterPagination
				toolbar={
					<RecentlyCreatedToolbar
						filterType={filterType}
						setFilterType={setFilterType}
						isMobile={isMobile}
					/>
				}
				loading={!projects.initialized}
				boxSx={{
					height: 'auto',
					maxHeight: isMobile ? '300px' : '400px',
					'& .MuiDataGrid-columnHeaders': {
						fontSize: isMobile ? '0.75rem' : 'inherit',
					},
					'& .MuiDataGrid-cell': {
						fontSize: isMobile ? '0.75rem' : 'inherit',
					},
				}}
				autoHeight={true}
				noRowsContent={
					filterType === 'ACTIVE'
						? 'Click the + New button to add a Project'
						: 'No projects have been archived yet'
				}
			/>
		</Paper>
	);
}

interface ToolbarProps {
	filterType: string;
	setFilterType: (value: string) => void;
	isMobile?: boolean;
}

const RecentlyCreatedToolbar = ({
	filterType,
	setFilterType,
	isMobile,
}: ToolbarProps) => {
	const toggleOptions = [
		{ value: 'ACTIVE', display: 'ACTIVE' },
		{ value: 'ARCHIVED', display: 'ARCHIVED' },
	];

	return (
		<GridToolbarContainer>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				width="100%"
				sx={{
					flexDirection: isMobile ? 'column' : 'row',
					gap: isMobile ? 1 : 0,
					padding: isMobile ? '0 8px' : 0,
				}}
			>
				<PaperHeader>Recent Projects</PaperHeader>
				<StyledToggleButton
					value={filterType}
					onChange={setFilterType}
					options={toggleOptions}
				/>
			</Box>
		</GridToolbarContainer>
	);
};

const StyledToggleButton = styled(ToggleButton)`
	margin-right: 10px;
`;
